/**
 * Pure part of "clear sessionStorage but keep the loop-bounding counters".
 *
 * This step is extracted because the bug it guards against shipped from here: the
 * WebView auth-recovery counter lives in sessionStorage, `endSession()` reaches
 * `clearSessionStorage()`, and wiping the counter reset the reload bound to 0 on every
 * load with a stale token — turning bounded recovery into an endless reload loop.
 *
 * The collect→restore decision is now testable without jsdom, which matters because
 * the vitest config only picks up `src/**\/*.logic.test.ts` in a node environment, so
 * a Storage-mocking test would not run at all today.
 */

/**
 * Snapshot the values that must survive a `sessionStorage.clear()`.
 *
 * Takes the reader as an argument rather than touching `sessionStorage` directly, so
 * it is deterministic and injectable. Absent keys are dropped rather than restored as
 * `null`, so a key that never existed is not resurrected as the string "null".
 */
export const collectPreservedEntries = (
  read: (key: string) => string | null,
  keys: readonly string[],
): Array<readonly [string, string]> => {
  const entries: Array<readonly [string, string]> = [];

  for (const key of keys) {
    let value: string | null = null;
    try {
      value = read(key);
    } catch {
      // A throwing reader (DOM storage disabled) means nothing to preserve, not a crash.
      continue;
    }
    if (value !== null && value !== undefined) {
      entries.push([key, value] as const);
    }
  }

  return entries;
};
